import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Tooltip, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';

import { ReactComponent as ApprovalIcon } from 'app/assets/img/approval.svg';

import { Layout } from '../layouts';
import { ACCOUNT_MNG_MENU } from '../layouts/AccountManager';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import DesktopSearch from './Search';
import StyledTable from 'app/components/StyledTable';
import { StyledStack } from 'app/components/Stack';
import useFetchData from 'app/hooks/useFetchData';
import { getDataAgentApi } from 'app/services/checkTicketApi';
import StyledPagination from 'app/components/StyledPagination';
import { formatNumber } from 'utils/helper';
import { Countdown } from '../layouts/ListTicketLayout';
import StatusComponent from 'app/components/StatusComponent';
import { getApprovalOrderListApi } from 'app/services/orderApi';
import styled from 'styled-components';
import FastApprovalModal from './FastApprovalModal';
import useGetDetailTicket from '../hooks/useGetDetailTicket';
import RenderIf from 'app/components/RenderIf';
import useWindowSize from 'app/hooks/useWindowSize';
import MobileSearch from './MobileSearch';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { Permission } from 'app/hooks/usePermission';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { NotiType } from 'app/components/Modals/DialogNotification';
import StyledTabs from 'app/components/StyledTabs';
import { HolidayOrderApprovalTab } from '../../OrderApproval/HolidayOrderApprovalTab';

export interface ApprovalOrderFilter {
  orderDate: string | null;
  orderId: string | number | null;
  agentId: string | null;
  subOrderState: string | null;
  page: number;
  limit: number;
}

export enum APPROVAL_ORDER_STATUS {
  PENDING = 'pendingConfirmation',
  SUCCESS = 'confirmed',
  REFUSE = 'unconfirmed',
  CANCELLED = 'cancelled',
}

export const APPROVAL_ORDER_STATUS_OPTIONS = [
  { label: 'b2b.approvalStt.pending', value: APPROVAL_ORDER_STATUS.PENDING },
  { label: 'b2b.approvalStt.success', value: APPROVAL_ORDER_STATUS.SUCCESS },
  { label: 'b2b.approvalStt.refuse', value: APPROVAL_ORDER_STATUS.REFUSE },
  {
    label: 'b2b.approvalStt.cancelled',
    value: APPROVAL_ORDER_STATUS.CANCELLED,
  },
];

export const removeNullishValue = (obj: Record<string, any>) => {
  const result = {};
  for (let key in obj) {
    const val = obj[key];
    if (!!val || val === 0) result[key] = val;
  }
  return result;
};

const ApproveOrder: React.FC = () => {
  const translations = useSelector(selectTranslations);
  const history = useHistory();
  const location = useLocation();
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';
  const infoUser = useSelector(selectInfoUser);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  // Tab state
  const href = window.location.href;
  const searchParams = new URL(href).searchParams;
  const tabRoute = searchParams.get('tab') || 'park';
  const [activeTab, setActiveTab] = useState<string>(tabRoute);

  // Permission check for Holiday tab
  // TODO: Temporarily disabled for UI testing
  // const infoUserAny = infoUser as any;
  // const hasHolidayPermission: boolean =
  //   (infoUserAny &&
  //     infoUserAny.businessPartner &&
  //     infoUserAny.businessPartner.allowBuyHoliday) ??
  //   false;
  const hasHolidayPermission: boolean = true; // Tạm thời luôn true để test UI

  const [agentData, setAgentData] = useState<
    { label: string; value: number }[]
  >([]);

  const { isLoading: isLoadingAgentData } = useFetchData({
    actionApi: getDataAgentApi,
    onSuccess: res => {
      setAgentData(
        res.data.map(agent => ({ label: agent.name, value: agent.id })),
      );
    },
  });

  const isAutoFetchApprovalOrderData = useRef(true);

  const [approvalOrderfilters, setApprovalOrderFilters] =
    useState<ApprovalOrderFilter>({
      orderDate: null,
      orderId: null,
      agentId: null,
      subOrderState: null,
      page: 1,
      limit: 20,
    });

  const [isLoadingApprovalData, setIsLoadingApprovalData] = useState(false);

  const isSaleMng =
    infoUser?.role_code === Permission.ROLE_SALE_MANAGER ||
    infoUser?.role_code === Permission.ROLE_SALE_SHG;

  const { result: approvalOrderData, onFetchData: onFetchApprovalOrder } =
    useFetchData({
      actionApi: getApprovalOrderListApi,
      payloadData: approvalOrderfilters,
      IIFE: isAutoFetchApprovalOrderData.current && isSaleMng,
      onBefore: () => {
        setIsLoadingApprovalData(true);
      },
      onSuccess: response => {
        if (!response?.data?.length && response?.paginator?.current > 1) {
          onFetchApprovalOrder({
            payloadData: removeNullishValue({
              ...approvalOrderfilters,
              page: response?.paginator?.current - 1,
            }),
          });
        } else {
          setIsLoadingApprovalData(false);
        }
      },
      onError: () => {
        setIsLoadingApprovalData(false);
      },
      onFinally: () => {
        isAutoFetchApprovalOrderData.current = false;
      },
    });

  const [fastApprovalData, setFastApprovalData] = useState<Record<
    string,
    any
  > | null>(null);

  const [warningMsg, setWarningMsg] = useState<ReactNode>(null);
  const [warningType, setWarningType] = useState<NotiType>('SUCCESS');

  const tableRef = useRef<any>();

  const onChangeApprovalOrderFilter = (
    filter: Partial<ApprovalOrderFilter>,
  ) => {
    setApprovalOrderFilters(prev => ({ ...prev, ...filter, page: 1 }));
  };

  const onFilterApprovalOrder = useCallback(
    (params?: Partial<ApprovalOrderFilter>) => {
      onFetchApprovalOrder({
        payloadData: removeNullishValue({ ...approvalOrderfilters, ...params }),
      });
    },
    [approvalOrderfilters, onFetchApprovalOrder],
  );

  const { getDetailTicket } = useGetDetailTicket({
    screen: '/approve-order',
    setIsLoading: setIsLoadingApprovalData,
  });

  const handleGoToDetailTicket = useCallback(
    async (idHash: string) => {
      const detailTicketData = await getDetailTicket(idHash);
      if (detailTicketData)
        history.push('/order-detail', {
          ticket: detailTicketData,
          screen: '/approve-order',
        });
    },
    [getDetailTicket, history],
  );

  const columns = useMemo(
    () => [
      {
        title: translations?.['b2b.txt.action'] ?? '',
        dataIndex: 'action',
        key: 'action',
        render: data => {
          const isDisable =
            data.subOrderState.value !== APPROVAL_ORDER_STATUS.PENDING;

          return (
            <div style={{ minWidth: 100 }}>
              <Tooltip
                color="white"
                title={translations?.['b2b.txt.approveNow'] ?? ''}
                overlayClassName="custom-tooltip"
              >
                <StyledApprovalIcon
                  $disable={isDisable}
                  onClick={() => !isDisable && setFastApprovalData(data)}
                />
              </Tooltip>
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.date-order'] ?? ''}
          </div>
        ),
        dataIndex: 'orderDate',
        key: 'orderDate',
        render: value => {
          const newDate = moment(value, 'HH:mm:ss DD/MM/YYYY');
          if (newDate.toString() === 'Invalid date')
            return <div style={{ minWidth: 100 }} />;
          return (
            <StyledStack justifyContent={'start'} style={{ minWidth: 100 }}>
              <div style={{ textAlign: 'left' }}>
                <Typography>{newDate.format('DD/MM/YYYY')}</Typography>
                <Typography>{newDate.format('HH:mm:ss')}</Typography>
              </div>
            </StyledStack>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2c.txt.orderNumber'] ?? ''}
          </div>
        ),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        render: value => {
          return (
            <div
              style={{ minWidth: 100, color: '#F8B117', cursor: 'pointer' }}
              onClick={handleGoToDetailTicket.bind(null, value?.idHash)}
            >
              {value?.id}
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.orderer'] ?? ''}
          </div>
        ),
        dataIndex: 'orderer',
        key: 'orderer',
        render: value => {
          return (
            <div style={{ minWidth: 100 }}>
              <TooltipParagraph
                ellipsis={{ rows: 1 }}
                style={{
                  marginBottom: 0,
                  maxWidth: 164,
                  textAlign: 'left',
                }}
              >
                {value}
              </TooltipParagraph>
            </div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>
            {translations?.['b2b.txt.order-type'] ?? ''}
          </div>
        ),
        dataIndex: 'orderType',
        key: 'orderType',
        render: value => {
          return (
            <div style={{ minWidth: 150, textAlign: 'center' }}>{value}</div>
          );
        },
      },
      {
        title: (
          <div style={{ textAlign: 'left' }}>
            {translations?.['b2b.txt.payIn'] ?? ''}
          </div>
        ),
        dataIndex: 'timePayIn',
        key: 'timePayIn',
        render: value => {
          if (
            value?.subOrderState?.value === APPROVAL_ORDER_STATUS.CANCELLED ||
            value?.subOrderState?.value === APPROVAL_ORDER_STATUS.SUCCESS
          )
            return <div style={{ minWidth: 150 }} />;

          return (
            <div style={{ minWidth: 150 }}>
              {value?.expiredAt && (
                <Countdown
                  onTimeout={() => {
                    const expiredAt = moment(
                      value?.expiredAt?.datetimeDMYHIS ?? undefined,
                      'HH:mm:ss DD/MM/YYYY',
                    );
                    const startTime = moment(
                      value?.startTime?.datetimeDMYHIS ?? undefined,
                      'HH:mm:ss DD/MM/YYYY',
                    );
                    const time = expiredAt.valueOf() - startTime.valueOf();
                    if (time <= 0) return;
                    onFilterApprovalOrder();
                  }}
                  orderCheckout={value}
                  noText
                />
              )}
            </div>
          );
        },
      },
      {
        title: (
          <table className="approval-order-title-table">
            <tbody>
              <tr>
                <TitleCell>{translations?.['b2c.txt.pttt'] ?? ''}</TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.total-discount'] ?? ''}
                </TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.total-amount'] ?? ''}
                </TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.order-status'] ?? ''}
                </TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.implementer'] ?? ''}
                </TitleCell>
                <TitleCell>
                  {translations?.['b2b.txt.approvalHistory'] ?? ''}
                </TitleCell>
              </tr>
            </tbody>
          </table>
        ),
        dataIndex: 'logConfirmation',
        key: 'logConfirmation',
        render: value => {
          return (
            <table className="approval-order-body-table">
              <tbody>
                {!value.length && (
                  <tr>
                    <BodyCell></BodyCell>
                    <BodyCell></BodyCell>
                    <BodyCell></BodyCell>
                    <BodyCell></BodyCell>
                    <BodyCell></BodyCell>
                    <BodyCell>
                      <span></span>
                      <span></span>
                    </BodyCell>
                  </tr>
                )}
                {value?.length > 0 &&
                  value.map((item, idx) => {
                    const [date, time] = item?.confirmedAt
                      ? item?.confirmedAt?.split(' ')
                      : '';
                    return (
                      <tr
                        key={`${item?.confirmer ?? 'na'}-${
                          item?.confirmedAt ?? 'na'
                        }-${idx}`}
                      >
                        <BodyCell>{item?.paymentMethod}</BodyCell>
                        <BodyCell>
                          {formatNumber(item?.totalDiscount).replaceAll(
                            ',',
                            '.',
                          )}
                          {'đ'}
                        </BodyCell>
                        <BodyCell>
                          {formatNumber(item?.totalPrice).replaceAll(',', '.')}
                          {'đ'}
                        </BodyCell>
                        <BodyCell>
                          <StatusComponent
                            status={item?.subOrderState?.value}
                            label={item?.subOrderState?.name}
                          />
                        </BodyCell>
                        <BodyCell>
                          <TooltipParagraph
                            ellipsis={{ rows: 1 }}
                            style={{
                              marginBottom: 0,
                              maxWidth: 164,
                            }}
                          >
                            {item?.confirmer}
                          </TooltipParagraph>
                        </BodyCell>
                        <BodyCell
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '50px',
                          }}
                        >
                          <span>{date ?? ''}</span>
                          <span>{time ?? ''}</span>
                        </BodyCell>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          );
        },
      },
    ],
    [onFilterApprovalOrder, translations, handleGoToDetailTicket],
  );

  const dataSource = useMemo(() => {
    const data = approvalOrderData?.data;

    return data?.map(order => {
      return {
        action: order,
        orderDate: order?.orderDate?.datetimeDMYHIS,
        orderNumber: order,
        orderer: order?.agent?.name,
        orderType: order?.orderType?.name,
        paymentMethod: order?.payment?.name,
        totalDiscount: order?.totalDiscount,
        totalAmount: order?.totalPrice,
        timePayIn: order,
        orderStt: order?.subOrderState,
        implementer: order,
        approvalHistory: order?.confirmedAt?.datetimeYHIS,
        logConfirmation: order?.logConfirmation ?? [],
      };
    });
  }, [approvalOrderData?.data]);

  useEffect(() => {
    if (warningMsg) {
      onOpenDialogNoti({
        type: warningType,
        title: '',
        content: warningMsg,
        isShow: true,
        maskClosable: true,
        closable: true,
        onCloseDialog: () => {
          onCloseDialogNoti();
          setWarningMsg(null);
          setWarningType('SUCCESS');
        },
      });
    }
  }, [onCloseDialogNoti, onOpenDialogNoti, warningMsg, warningType]);

  if (infoUser?.role_code && !isSaleMng) {
    history.replace('/page-not-found');
    return null;
  }

  return (
    <>
      <Layout
        title={translations?.['b2b.txt.ordersNeedApproval'] ?? ''}
        menuActive={ACCOUNT_MNG_MENU.APPROVE_ORDER}
      >
        {!infoUser?.role_code ? (
          <LoadingContainer loading />
        ) : (
          <StyledTabs
            activeKey={activeTab}
            onChange={tab => {
              setActiveTab(tab);
              history.push(`?tab=${tab}`);
            }}
          >
            <StyledTabs.TabPane
              tab={
                translations?.['b2b.txt.parkTicketOrders'] ??
                'Đơn hàng vé công viên'
              }
              key="park"
            >
              <RenderIf
                condition={isDesktop}
                fallback={
                  <MobileSearch
                    approvalOrderFilter={approvalOrderfilters}
                    onChangeFilter={onChangeApprovalOrderFilter}
                    onSubmitFilter={onFilterApprovalOrder}
                    agentData={agentData}
                  />
                }
              >
                <DesktopSearch
                  approvalOrderFilter={approvalOrderfilters}
                  onChangeFilter={onChangeApprovalOrderFilter}
                  onSubmitFilter={onFilterApprovalOrder}
                  agentData={agentData}
                />
              </RenderIf>
              <div style={{ marginTop: 24 }}>
                <StyledTable
                  tableRef={tableRef}
                  dataSource={dataSource}
                  columns={columns}
                  pagination={false}
                  tableProps={{
                    scroll: {
                      x: dataSource?.length ? 'auto' : 'max-content',
                      y: dataSource?.length ? '50vh' : undefined,
                    },
                    loading: {
                      spinning: isLoadingApprovalData,
                      indicator: (
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      ),
                    },
                  }}
                  styleSlots={{
                    antTableContainer: {
                      overflow: 'hidden',
                    },
                    antTableHeader: {
                      border: 'none !important',
                      borderRadius: '0 !important',
                    },
                    tableCSS: {
                      borderTopLeftRadius: !dataSource?.length
                        ? ''
                        : '0 !important',
                      borderTopRightRadius: !dataSource?.length
                        ? ''
                        : '0 !important',
                    },
                    antTableBody: {
                      paddingBottom: 10,
                    },
                  }}
                  emptyText={translations?.['b2b.txt.noOrderFound'] ?? ''}
                />
                <StyledPagination
                  style={{
                    marginTop: '40px',
                    display: 'flex',
                  }}
                  showSizeChanger
                  defaultCurrent={1}
                  current={approvalOrderfilters?.page || 1}
                  total={approvalOrderData?.paginator?.totalCount || 0}
                  pageSize={approvalOrderfilters.limit || 20}
                  onChange={(page, pageSize) => {
                    const newApprovalOrderFilter: ApprovalOrderFilter = {
                      ...approvalOrderfilters,
                      page,
                      limit: pageSize,
                    };

                    setApprovalOrderFilters(newApprovalOrderFilter);
                    onFetchApprovalOrder({
                      payloadData: newApprovalOrderFilter,
                      callback: {
                        onFinally: () => {
                          window.scrollTo(0, 0);
                          tableRef?.current
                            ?.querySelector('.ant-table-body')
                            ?.scrollTo({
                              top: 0,
                              behavior: 'smooth',
                            });
                        },
                      },
                    });
                  }}
                  pageSizeOptions={['20', '50', '100']}
                />
              </div>
            </StyledTabs.TabPane>

            <StyledTabs.TabPane
              tab={
                translations?.['b2b.txt.holidayComboOrders'] ??
                'Đơn hàng vé Combo Holiday'
              }
              key="holiday"
            >
              {hasHolidayPermission ? (
                <HolidayOrderApprovalTab />
              ) : (
                <div
                  style={{
                    padding: '40px 0',
                    textAlign: 'center',
                    color: '#666',
                  }}
                >
                  {translations?.['MSG020'] ??
                    'Bạn không có quyền truy cập chức năng này'}
                </div>
              )}
            </StyledTabs.TabPane>
          </StyledTabs>
        )}
      </Layout>
      <FastApprovalModal
        data={fastApprovalData}
        onCloseModal={() => setFastApprovalData(null)}
        onFilterApprovalOrder={onFilterApprovalOrder}
        setWarningMsg={setWarningMsg}
        setWarningType={setWarningType}
      />
    </>
  );
};

export default ApproveOrder;

const StyledApprovalIcon = styled(ApprovalIcon)<{ $disable?: boolean }>`
  cursor: ${props => (props.$disable ? 'auto' : 'pointer')};
  rect {
    fill: transparent;
  }
  path {
    stroke: ${props => (props.$disable ? '#D9D9D9' : '#FF7B01')};
  }
`;

const TitleCell = styled.td`
  padding: 15px 10px;
  min-width: 150px;
`;

const BodyCell = styled.td`
  max-width: 150px;
  min-width: 150px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;
